import type { ButtonHTMLAttributes, CSSProperties, InputHTMLAttributes, ReactNode } from "react";
import { t } from "../i18n";
import { Icon, PixelText } from "./pixel";

/** Fonction d'une touche, qui fixe sa couleur : cyan action, ambre rustine, vert réparation, corail danger. */
export type KeyTone = "neutral" | "action" | "patch" | "repair" | "danger" | "quiet";

/** Encre corail d'une touche discrète qui défait quelque chose (démanteler, supprimer) : à part, et visible. */
export const DANGER_INK = { "--ink": "var(--color-stop)" } as CSSProperties;

/** Une touche de borne : elle s'enfonce d'un pixel quand on l'appuie. */
export function Key({
  tone = "neutral",
  size = "md",
  className = "",
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { tone?: KeyTone; size?: "sm" | "md" | "icon" }) {
  return (
    <button
      type="button"
      className={`key ${className}`}
      data-tone={tone === "neutral" ? undefined : tone}
      data-size={size === "md" ? undefined : size}
      {...props}
    >
      {children}
    </button>
  );
}

/**
 * En-tête d'une fiche, lu comme l'afficheur d'une borne : le titre allumé en police bitmap dans un creux,
 * la touche pour fermer à côté, puis ce qui situe la fiche.
 */
export function Plate({ title, onClose, children }: { title: string; onClose?: (() => void) | undefined; children?: ReactNode }) {
  return (
    <header className="dotted pb-3">
      <div className="flex items-stretch gap-2">
        <h2 className="well flex min-w-0 flex-1 items-center gap-2.5 px-3 py-2.5 text-accent">
          <Led on color="var(--color-accent)" />
          <PixelText text={title} />
        </h2>
        {onClose ? (
          <Key size="icon" tone="quiet" onClick={onClose} aria-label={t("panel.close")} title={t("panel.close")}>
            <Icon name="close" />
          </Key>
        ) : null}
      </div>
      {children}
    </header>
  );
}

/** Titre d'une partie de fiche, en police bitmap suivi d'une ligne pointillée (tableau des scores), et sa consigne. */
export function SectionTitle({ children, hint }: { children: string; hint?: ReactNode }) {
  return (
    <div className="mb-2 mt-6 first:mt-0">
      <h3 className="flex items-center gap-2 text-dim">
        <PixelText text={children} />
        <span aria-hidden="true" className="dotted h-[var(--px)] flex-1 bg-center" />
      </h3>
      {hint ? <p className="mt-1.5 text-[0.9375rem] text-muted">{hint}</p> : null}
    </div>
  );
}

/** Voyant : éteint, allumé, ou clignotant par paliers (ce qui presse). */
export function Led({ on, color, blink = false, className = "" }: { on: boolean; color?: string; blink?: boolean; className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={`led ${on && blink ? "blink" : ""} ${className}`}
      data-on={on}
      style={color ? ({ "--led": color } as CSSProperties) : undefined}
    />
  );
}

/** Jauge à segments dans son creux ; `value` entre 0 et 1. */
export function Gauge({
  value,
  color,
  label,
  segments = 10,
  className = "",
}: {
  value: number;
  color: string;
  label: string;
  segments?: number;
  className?: string;
}) {
  const ratio = Math.min(1, Math.max(0, value));
  // Un reste, même minime, allume un segment : une jauge vide doit vouloir dire « rien ».
  const lit = ratio > 0 ? Math.max(1, Math.round(ratio * segments)) : 0;
  return (
    <div
      className={`well segments ${className}`}
      role="meter"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(ratio * 100)}
      aria-label={label}
      style={{ "--seg": color } as CSSProperties}
    >
      {Array.from({ length: segments }, (_, index) => (
        <span key={index} className="segment" data-on={index < lit} />
      ))}
    </div>
  );
}

const DITHER = {
  // Matrice de Bayer 4 × 4 : la densité du tramage monte avec l'usure.
  bayer: [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5],
} as const;

/**
 * Usure en densité de tramage : chaque segment usé se remplit d'un tramage d'autant plus dense que le
 * module est fatigué (clairsemé en bon état, serré quand il est usé, plein au seuil critique).
 */
export function WearGauge({ wear, color, label, level }: { wear: number; color: string; label: string; level: "ok" | "worn" | "critical" }) {
  const segments = 12;
  const ratio = Math.min(1, Math.max(0, wear));
  const lit = ratio > 0 ? Math.max(1, Math.round(ratio * segments)) : 0;
  const threshold = level === "critical" ? 16 : level === "worn" ? 8 : 4;
  const cellW = 4;
  const cellH = 4;
  let d = "";
  for (let segment = 0; segment < lit; segment++) {
    for (let y = 0; y < cellH; y++) {
      for (let x = 0; x < cellW - 1; x++) {
        const gx = segment * cellW + x;
        if ((DITHER.bayer[(y % 4) * 4 + (gx % 4)] ?? 0) < threshold) d += `M${gx} ${y}h1v1h-1z`;
      }
    }
  }
  let empty = "";
  for (let segment = lit; segment < segments; segment++) empty += `M${segment * cellW} 0h${cellW - 1}v${cellH}h${1 - cellW}z`;
  const width = segments * cellW - 1;
  return (
    <div className="well inline-flex p-[calc(var(--px)*2)]" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(ratio * 100)} aria-label={label}>
      <svg
        viewBox={`0 0 ${width} ${cellH}`}
        aria-hidden="true"
        shapeRendering="crispEdges"
        className="pixel-svg"
        style={{ width: `calc(var(--px) * ${width})`, height: `calc(var(--px) * ${cellH})` }}
      >
        <path d={empty} fill="#18213c" />
        <path d={d} fill={color} />
      </svg>
    </div>
  );
}

/** Rangée d'un tableau des scores : libellé à gauche, valeur en chiffres bitmap à droite. */
export function ScoreRow({ label, value, tone }: { label: ReactNode; value: string; tone?: string | undefined }) {
  return (
    <div className="dotted flex items-center justify-between gap-3 py-2.5">
      <dt className="text-[0.9375rem] text-muted">{label}</dt>
      <dd style={tone ? { color: tone } : undefined} className={tone ? "" : "text-ink"}>
        <PixelText text={value} />
      </dd>
    </div>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <p className="well px-4 py-5 text-center text-[0.9375rem] text-muted">{children}</p>;
}

/** Champ de saisie dans son creux (le contour s'allume au focus). */
export function Field({ className = "", ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div className="well">
      <input className={`field ${className}`} {...props} />
    </div>
  );
}
